// 登录页 —— 两个 tabs：密码（默认，更快）/ Magic link。
// "忘记密码？" → 跳 /forgot-password 独立流程。

import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { AuthApiError, requestMagicLink } from "../lib/auth-api";
import { takePendingInvite } from "../lib/invite-api";
import { useAuth } from "../lib/use-auth";

type Tab = "password" | "magic-link";
type PwMode = "login" | "signup";
const REMEMBER_EMAIL_KEY = "chatbox.login.email";
const REMEMBER_PASSWORD_KEY = "chatbox.login.password";

type MlStage =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "sent"; email: string }
  | { kind: "error"; message: string };

export function LoginPage() {
  const { status, signInWithPassword, signUpWithPassword } = useAuth();
  const [tab, setTab] = useState<Tab>("password");
  const nav = useNavigate();

  if (status === "authenticated") {
    const pending = takePendingInvite();
    return <Navigate to={pending ? `/invite/${pending}` : "/rooms"} replace />;
  }

  return (
    <section className="page page--login">
      <h1>欢迎回到 ChatBox</h1>
      <div className="tabs">
        <button
          className={tab === "password" ? "tab tab--active" : "tab"}
          onClick={() => setTab("password")}
        >
          密码登录
        </button>
        <button
          className={tab === "magic-link" ? "tab tab--active" : "tab"}
          onClick={() => setTab("magic-link")}
        >
          Magic link
        </button>
      </div>

      {tab === "password" && (
        <PasswordForm
          onForgot={() => nav("/forgot-password")}
          onLogin={signInWithPassword}
          onSignup={signUpWithPassword}
        />
      )}
      {tab === "magic-link" && <MagicLinkForm />}
    </section>
  );
}

interface PasswordFormProps {
  onForgot(): void;
  onLogin(email: string, password: string): Promise<void>;
  onSignup(email: string, password: string): Promise<void>;
}

function PasswordForm({ onForgot, onLogin, onSignup }: PasswordFormProps) {
  const [mode, setMode] = useState<PwMode>("login");
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_EMAIL_KEY) ?? "");
  const [password, setPassword] = useState(() => localStorage.getItem(REMEMBER_PASSWORD_KEY) ?? "");
  const [rememberEmail, setRememberEmail] = useState(
    () => !!localStorage.getItem(REMEMBER_EMAIL_KEY),
  );
  const [rememberPassword, setRememberPassword] = useState(
    () => !!localStorage.getItem(REMEMBER_PASSWORD_KEY),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setError(null);
    try {
      const nextEmail = email.trim();
      if (mode === "login") await onLogin(nextEmail, password);
      else await onSignup(nextEmail, password);
      saveRememberedCredentials(nextEmail, password, rememberEmail, rememberPassword);
    } catch (err) {
      setError(
        err instanceof AuthApiError
          ? err.serverMessage
          : err instanceof Error
            ? err.message
            : "未知错误",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <label className="form__row">
        <span>邮箱</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          required
          disabled={busy}
          autoFocus
        />
      </label>
      <label className="form__row">
        <span>
          密码 {mode === "signup" && <small>（≥ 8 字符，含字母+数字）</small>}
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          required
          minLength={8}
          disabled={busy}
        />
      </label>
      <div className="form__remember">
        <label>
          <input
            type="checkbox"
            checked={rememberEmail}
            disabled={busy}
            onChange={(e) => {
              const checked = e.target.checked;
              setRememberEmail(checked);
              if (!checked) setRememberPassword(false);
            }}
          />
          <span>记住账号</span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={rememberPassword}
            disabled={busy}
            onChange={(e) => {
              const checked = e.target.checked;
              setRememberPassword(checked);
              if (checked) setRememberEmail(true);
            }}
          />
          <span>记住密码</span>
        </label>
      </div>

      <div className="form__actions">
        <button
          className="btn btn--primary"
          type="submit"
          disabled={busy || !email.trim() || !password}
        >
          {busy ? (mode === "login" ? "登录中…" : "注册中…") : mode === "login" ? "登录" : "注册"}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            setMode(mode === "login" ? "signup" : "login");
            setError(null);
          }}
          disabled={busy}
        >
          {mode === "login" ? "还没账号？立刻注册 →" : "← 已有账号？登录"}
        </button>
      </div>

      {mode === "login" && (
        <div className="form__secondary">
          <button type="button" className="btn btn--ghost link" onClick={onForgot}>
            忘记密码？
          </button>
        </div>
      )}

      {error && (
        <div className="login-hint login-hint--error">
          <p>{error}</p>
        </div>
      )}
    </form>
  );
}

function saveRememberedCredentials(
  email: string,
  password: string,
  rememberEmail: boolean,
  rememberPassword: boolean,
) {
  if (rememberEmail || rememberPassword) {
    localStorage.setItem(REMEMBER_EMAIL_KEY, email);
  } else {
    localStorage.removeItem(REMEMBER_EMAIL_KEY);
  }
  if (rememberPassword) {
    localStorage.setItem(REMEMBER_PASSWORD_KEY, password);
  } else {
    localStorage.removeItem(REMEMBER_PASSWORD_KEY);
  }
}

function MagicLinkForm() {
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<MlStage>({ kind: "idle" });

  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setStage({ kind: "submitting" });
    try {
      await requestMagicLink(value);
      setStage({ kind: "sent", email: value });
    } catch (err) {
      const message =
        err instanceof AuthApiError
          ? err.serverMessage
          : err instanceof Error
            ? err.message
            : "未知错误";
      setStage({ kind: "error", message });
    }
  }

  const sent = stage.kind === "sent";

  return (
    <form className="form" onSubmit={submit}>
      <label className="form__row">
        <span>邮箱</span>
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={stage.kind === "submitting" || sent}
          required
          autoFocus
        />
      </label>

      <div className="form__actions">
        <button
          className="btn btn--primary"
          type="submit"
          disabled={stage.kind === "submitting" || sent || !email.trim()}
        >
          {stage.kind === "submitting" ? "发送中…" : sent ? "已发送" : "发送登录链接"}
        </button>
      </div>

      {sent && (
        <div className="login-hint">
          <p>
            📧 已发送链接到 <code>{stage.email}</code>，请查收邮件并在 10 分钟内点击。
          </p>
        </div>
      )}

      {stage.kind === "error" && (
        <div className="login-hint login-hint--error">
          <p>发送失败：{stage.message}</p>
        </div>
      )}
    </form>
  );
}
